import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "../lib/supabase";

const STATUS_STYLES = {
  LOW:      "bg-red-50 text-red-600 border border-red-100",
  HIGH:     "bg-orange-50 text-orange-600 border border-orange-100",
  NORMAL:   "bg-green-50 text-green-600 border border-green-100",
  NEGATIVE: "bg-gray-100 text-gray-500 border border-gray-200",
  ABNORMAL: "bg-red-50 text-red-600 border border-red-100",
  POSITIVE: "bg-orange-50 text-orange-600 border border-orange-100",
};

const FLAGGED = new Set(["LOW", "HIGH", "ABNORMAL", "POSITIVE"]);

function fmt(date) {
  return new Date(date + "T12:00:00").toLocaleDateString("en-US", {
    month: "long", day: "numeric", year: "numeric",
  });
}

// Keep only the most recent result per marker
function latestPerMarker(rows) {
  const map = {};
  for (const row of rows) {
    if (!map[row.marker] || row.test_date > map[row.marker].test_date) {
      map[row.marker] = row;
    }
  }
  return Object.values(map).sort((a, b) => a.marker.localeCompare(b.marker));
}

function StatCard({ label, value, unit }) {
  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-5">
      <p className="text-[10px] tracking-[0.2em] uppercase text-gray-400 font-medium mb-1">{label}</p>
      <p className="text-3xl font-bold text-black leading-tight">
        {value}<span className="text-base font-normal text-gray-400 ml-1">{unit}</span>
      </p>
    </div>
  );
}

function LabRow({ row }) {
  const ref = [row.reference_low, row.reference_high].filter((v) => v != null).join(" – ");
  return (
    <tr className="border-t border-gray-50 hover:bg-gray-50 transition-colors">
      <td className="py-3 pl-5 pr-4 text-sm font-medium text-black">
        {row.marker}
        <span className="ml-2 text-[10px] text-gray-300">{fmt(row.test_date)}</span>
      </td>
      <td className="py-3 pr-4 text-sm text-gray-700 tabular-nums">
        {row.value != null
          ? <>{row.value} <span className="text-gray-400 text-xs">{row.unit}</span></>
          : <span className="text-gray-300">—</span>}
      </td>
      <td className="py-3 pr-4 text-xs text-gray-400 tabular-nums">{ref || "—"}</td>
      <td className="py-3 pr-5">
        {row.status
          ? <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS_STYLES[row.status] ?? "bg-gray-100 text-gray-500"}`}>{row.status}</span>
          : <span className="text-gray-300 text-xs">—</span>}
      </td>
    </tr>
  );
}

function LabTable({ rows }) {
  return (
    <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="bg-gray-50">
            <th className="text-left text-[10px] tracking-[0.15em] uppercase text-gray-400 font-medium py-3 px-5">Marker</th>
            <th className="text-left text-[10px] tracking-[0.15em] uppercase text-gray-400 font-medium py-3 pr-4">Value</th>
            <th className="text-left text-[10px] tracking-[0.15em] uppercase text-gray-400 font-medium py-3 pr-4">Ref Range</th>
            <th className="text-left text-[10px] tracking-[0.15em] uppercase text-gray-400 font-medium py-3 pr-5">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => <LabRow key={row.id} row={row} />)}
        </tbody>
      </table>
    </div>
  );
}

export default function Health() {
  const [vitals, setVitals]    = useState([]);
  const [labs, setLabs]        = useState([]);
  const [conditions, setConds] = useState([]);
  const [loading, setLoading]  = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    async function load() {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { navigate("/login"); return; }
      const [v, l, c] = await Promise.all([
        supabase.from("vitals").select("*").order("visit_date", { ascending: false }),
        supabase.from("lab_results").select("*").order("test_date", { ascending: false }).order("marker"),
        supabase.from("conditions").select("*").order("name"),
      ]);
      setVitals(v.data ?? []);
      setLabs(l.data ?? []);
      setConds(c.data ?? []);
      setLoading(false);
    }
    load();
  }, [navigate]);

  const latest = vitals[0];
  const recentLabs = latestPerMarker(labs);
  const flagged = recentLabs.filter((r) => FLAGGED.has(r.status));
  const allOther = recentLabs.filter((r) => !FLAGGED.has(r.status));
  const year = new Date().getFullYear();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-sm text-gray-400 animate-pulse">Loading health data…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      <div className="max-w-4xl mx-auto px-6 py-12">

        <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-black transition-colors mb-6">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-3 w-3">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
          </svg>
          Dashboard
        </Link>
        <p className="text-[10px] tracking-[0.22em] uppercase text-gray-400 font-medium mb-3">Dashboard · Health</p>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-black mb-10">Health</h1>

        {/* Latest Vitals */}
        {latest && (
          <section className="mb-10">
            <h2 className="text-xs tracking-[0.18em] uppercase text-gray-400 font-semibold mb-4">
              Latest Vitals
              <span className="ml-2 normal-case tracking-normal font-normal text-gray-300">{fmt(latest.visit_date)}</span>
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              <StatCard label="Blood Pressure" value={`${latest.blood_pressure_systolic}/${latest.blood_pressure_diastolic}`} unit="mmHg" />
              <StatCard label="Pulse" value={latest.pulse} unit="bpm" />
              <StatCard label="Weight" value={latest.weight_lbs} unit="lb" />
              <StatCard label="BMI" value={latest.bmi} />
              <StatCard label="Temperature" value={latest.temperature_f} unit="°F" />
              <StatCard label="SpO₂" value={latest.spo2} unit="%" />
              <StatCard label="Height" value={`${Math.floor(latest.height_in / 12)}'${latest.height_in % 12}"`} />
            </div>
          </section>
        )}

        {/* Conditions */}
        {conditions.length > 0 && (
          <section className="mb-10">
            <h2 className="text-xs tracking-[0.18em] uppercase text-gray-400 font-semibold mb-4">Active Conditions</h2>
            <div className="flex flex-wrap gap-2">
              {conditions.map((c) => (
                <span key={c.id} className="text-sm px-4 py-2 bg-white border border-gray-100 rounded-full text-gray-700">
                  {c.name}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* Flagged results */}
        {flagged.length > 0 && (
          <section className="mb-10">
            <h2 className="text-xs tracking-[0.18em] uppercase text-gray-400 font-semibold mb-4">Needs Attention</h2>
            <LabTable rows={flagged} />
          </section>
        )}

        {/* All other lab results */}
        {allOther.length > 0 && (
          <section className="mb-10">
            <h2 className="text-xs tracking-[0.18em] uppercase text-gray-400 font-semibold mb-4">Lab Results</h2>
            <LabTable rows={allOther} />
          </section>
        )}

        {/* Vitals History */}
        {vitals.length > 1 && (
          <section className="mb-10">
            <h2 className="text-xs tracking-[0.18em] uppercase text-gray-400 font-semibold mb-4">Vitals History</h2>
            <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    {["Date", "BP", "Pulse", "Weight", "BMI", "Temp", "SpO₂"].map((h) => (
                      <th key={h} className="text-left text-[10px] tracking-[0.15em] uppercase text-gray-400 font-medium py-3 px-4 first:pl-5 last:pr-5">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {vitals.map((v) => (
                    <tr key={v.id} className="border-t border-gray-50 hover:bg-gray-50 transition-colors text-sm">
                      <td className="py-3 pl-5 pr-4 text-gray-500 whitespace-nowrap">{fmt(v.visit_date)}</td>
                      <td className="py-3 pr-4 tabular-nums text-gray-700">{v.blood_pressure_systolic}/{v.blood_pressure_diastolic}</td>
                      <td className="py-3 pr-4 tabular-nums text-gray-700">{v.pulse}</td>
                      <td className="py-3 pr-4 tabular-nums text-gray-700">{v.weight_lbs} lb</td>
                      <td className="py-3 pr-4 tabular-nums text-gray-700">{v.bmi}</td>
                      <td className="py-3 pr-4 tabular-nums text-gray-700">{v.temperature_f}°F</td>
                      <td className="py-3 pr-5 tabular-nums text-gray-700">{v.spo2}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

      </div>

      <p className="text-[10px] text-gray-300 text-center pb-2">
        © {year} Dylan Zajac. All rights reserved.
      </p>
    </div>
  );
}
